import { CUSTOM_ELEMENTS_SCHEMA, ModuleWithProviders, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
// Angular Material
import { MatTabsModule } from '@angular/material/tabs';
import { DragDropModule } from '@angular/cdk/drag-drop';
// Others
import { NgxUploaderModule } from 'ngx-uploader';
// Components
import {
  AlertComponent,
  ToastComponent,
  AccountMenuComponent,
  AgentChipComponent,
  BreadcrumbComponent,
  ChangeGroupModalComponent,
  ChartComponent,
  ClientsPublishSelectorComponent,
  ButtonSelectComponent,
  CourseItemComponent,
  CoursesSelectorComponent,
  CsvDownloadComponent,
  CsvImportComponent,
  JobNotificationsComponent,
  JobNotificationsMessagesComponent,
  ChooserColumnComponent,
  DynamicFormComponent,
  DynamicFormQuestionComponent,
  DynamicFormQuestionInfoComponent,
  DynamicFormErrorComponent,
  EditTagsComponent,
  ExtendComponent,
  FeatureDiscoveryComponent,
  FilterTogglerComponent,
  FooterComponent,
  HeaderComponent,
  ImageUploaderLargeComponent,
  ImageUploaderPositionerComponent,
  ImageUploaderSmallComponent,
  InfoExpirationDateComponent,
  ItemsCountSelectorComponent,
  ListComponent,
  ListFilterRowComponent,
  ListFiltersComponent,
  ListHeaderComponent,
  ListPaginatorComponent,
  ListCounterComponent,
  ListRowDetailComponent,
  ListRowSelectorComponent,
  NavComponent,
  PersonalizedEmailComponent,
  PreloaderComponent,
  PrintComponent,
  RangeComponent,
  ReportComponent,
  SearchComponent,
  SearchFieldComponent,
  SimpleUploaderComponent,
  ToolbarComponent,
  AdminRoleChangerComponent,
  WizardComponent,
  WizardStepComponent,
  SelectDropdownComponent,
  ChartSpeedometerComponent,
  VisorImageComponent,
  EmailPreviewComponent,
  DocumentsUploaderComponent,
  PieChartComponent,
  ImageSelectorComponent,
  ModalComponent
} from './components';
import { SkeletonAppComponent } from 'core/components/skeleton-app/skeleton-app.component';

// Pipes
import {
  AgentNamePipe,
  AgentRoleFilter,
  CountryNamePipe,
  DatePipe,
  DurationPipe,
  FieldReportPipe,
  LocaleNamePipe,
  InitialsPipe,
  ShowFilterPipe
} from './pipes';
// Services
import {
  AgentsCacheService,
  AgentsService,
  ArcadeService,
  CataloguesService,
  ClientsService,
  CoreFiltersService,
  CoreQuestionsFactoryService,
  CountriesService,
  CourseDocumentsService,
  CustomHttpService,
  DistributorsService,
  EmitterService,
  ErrorHandlerService,
  GamesService,
  GroupsService,
  ItinerariesService,
  JwtService,
  JobsService,
  JournalService,
  LicensesActivityService,
  LicensesService,
  ListHelperService,
  LocalesService,
  OrdersService,
  ProfileService,
  DashboardService,
  PopUpService,
  ToastAlertService,
  RemoteLoggingService,
  SecurityService,
  StoreService,
  StudentsService,
  SubscriptionsService,
  TasksService,
  TrailService,
  NotificationsService,
  ToastService,
  UiService,
  UsersService,
  UtilsService,
  ImageConversionService,
  CustomizationsService,
  AdaCustomizationsService,
  Game2100CustomizationsService,
  MarsCustomizationsService,
  MicrofrontendsService,
  ConfigurationsService,
  UtilsQuestionsService,
  XlsxService
} from './services';
import { QuestionControlService } from 'core/services/forms/question-control.service';
// Resolvers
import { CalculatePercentagePipe } from './pipes/calculate-percentage/calculate-percentage.pipe';
import { CheckNoDataPipe } from './pipes/check-no-data/check-no-data.pipe';
import { CalcDivisionPipe } from './pipes/calc-division/calc-division.pipe';
import { CalcRotatePipe } from './pipes/calc-rotate/calc-rotate.pipe';
import { SelectDashboardComponent } from './components/select-dashboard/select-dashboard.component';
import { ButtonActionsComponent } from './components/button-actions/button-actions.component';
import { NoticeComponent } from './components/notice/notice.component';
import { ButtonSpinnerComponent } from './components/button-spinner/button-spinner.component';
import { BaseChartDirective } from 'ng2-charts';
import { DirectivesModule } from '@directives/directives.module';
import { ImageCropperModule } from 'core/components/image-selector/image-cropper.module';

const modules = [
  CommonModule,
  FormsModule,
  ReactiveFormsModule,
  NgxUploaderModule,
  RouterModule,
  TranslateModule,
  MatTabsModule,
  DragDropModule,
  DirectivesModule,
  BaseChartDirective,
  ImageSelectorComponent,
  ImageCropperModule
];

const components = [
  AlertComponent,
  ToastComponent,
  AccountMenuComponent,
  AgentChipComponent,
  BreadcrumbComponent,
  ChangeGroupModalComponent,
  ChartComponent,
  ChartSpeedometerComponent,
  ClientsPublishSelectorComponent,
  ButtonSelectComponent,
  CourseItemComponent,
  CoursesSelectorComponent,
  CsvDownloadComponent,
  CsvImportComponent,
  JobNotificationsComponent,
  JobNotificationsMessagesComponent,
  ChooserColumnComponent,
  DocumentsUploaderComponent,
  DynamicFormComponent,
  DynamicFormErrorComponent,
  DynamicFormQuestionComponent,
  DynamicFormQuestionInfoComponent,
  ExtendComponent,
  EditTagsComponent,
  FeatureDiscoveryComponent,
  FilterTogglerComponent,
  FooterComponent,
  HeaderComponent,
  ImageUploaderLargeComponent,
  ImageUploaderPositionerComponent,
  ImageUploaderSmallComponent,
  InfoExpirationDateComponent,
  ItemsCountSelectorComponent,
  ListComponent,
  ListFilterRowComponent,
  ListFiltersComponent,
  ListHeaderComponent,
  ListPaginatorComponent,
  ListCounterComponent,
  ListRowDetailComponent,
  ListRowSelectorComponent,
  ModalComponent,
  NavComponent,
  PersonalizedEmailComponent,
  EmailPreviewComponent,
  PreloaderComponent,
  PrintComponent,
  RangeComponent,
  ReportComponent,
  SearchComponent,
  SearchFieldComponent,
  SimpleUploaderComponent,
  ToolbarComponent,
  AdminRoleChangerComponent,
  WizardComponent,
  WizardStepComponent,
  SelectDropdownComponent,
  VisorImageComponent,
  SelectDashboardComponent,
  ButtonActionsComponent,
  ButtonSpinnerComponent,
  PieChartComponent,
  NoticeComponent,
  SkeletonAppComponent
];

const pipes = [
  AgentNamePipe,
  AgentRoleFilter,
  CountryNamePipe,
  DurationPipe,
  FieldReportPipe,
  LocaleNamePipe,
  InitialsPipe,
  DatePipe,
  ShowFilterPipe,
  CalculatePercentagePipe,
  CheckNoDataPipe,
  CalcDivisionPipe,
  CalcRotatePipe
];

const services = [
  AgentsCacheService,
  AgentsService,
  ArcadeService,
  CataloguesService,
  ClientsService,
  CoreFiltersService,
  CoreQuestionsFactoryService,
  CountriesService,
  CourseDocumentsService,
  CustomHttpService,
  DistributorsService,
  EmitterService,
  ErrorHandlerService,
  GamesService,
  GroupsService,
  ItinerariesService,
  MicrofrontendsService,
  JobsService,
  JournalService,
  JwtService,
  LicensesActivityService,
  LicensesService,
  ListHelperService,
  LocalesService,
  OrdersService,
  ProfileService,
  DashboardService,
  QuestionControlService,
  PopUpService,
  ToastAlertService,
  RemoteLoggingService,
  SecurityService,
  StoreService,
  StudentsService,
  SubscriptionsService,
  TasksService,
  TrailService,
  NotificationsService,
  ToastService,
  UiService,
  UsersService,
  UtilsService,
  ImageConversionService,
  ConfigurationsService,
  CustomizationsService,
  AdaCustomizationsService,
  Game2100CustomizationsService,
  MarsCustomizationsService,
  UtilsQuestionsService,
  XlsxService
];

@NgModule({
  imports: [...modules],
  declarations: [...components, ...pipes],
  exports: [...modules, ...components, ...pipes],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CoreModule {
  static forRoot(): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [...services]
    };
  }
}
